import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { StarFilled, CheckCircleOutlined, WarningOutlined, ClockCircleOutlined, InboxOutlined, EditOutlined, FileTextOutlined, MailOutlined, SettingOutlined, TagOutlined } from '@ant-design/icons';
import axiosInstance from '../../variables/api/axiosInstance';
import { getUser } from '../../variables/constants';
import SidebarDossiers from './SidebarDossiers';
import ListeConversations from './ListeConversations';
import LecteurConversation from './LecteurConversation';
import ComposeWindow from './ComposeWindow';
import SearchOverlay from './SearchOverlay';
import ParametresMessagerie from './ParametresMessagerie';
import TopBar from './TopBar';
import FicheSinistre from './FicheSinistre';
import { estPagine, lirePage, fusionnerPages, resteDesPages } from './paginationInbox';
import { categorieAffichee } from './categorieMessage';
import T from './theme';

import i18n from '../../i18n';
import { fenetreRedaction } from './fenetreRedaction';

const FOLDER_EP = {
  inbox: '/v1/messagerie/inbox',
  sent: '/v1/messagerie/sent',
  drafts: '/v1/messagerie/drafts',
  relances: '/v1/messagerie/relances',
  garanties: '/v1/messagerie/garanties',
  expertises: '/v1/messagerie/expertises',
  bap: '/v1/messagerie/bap',
  bordereaux: '/v1/messagerie/bordereaux',
  suivis: '/v1/messagerie/suivis',
  snooze: '/v1/messagerie/snooze',
  trash: '/v1/messagerie/trash',
};

function parseFrom(raw) {
  if (!raw) return { name: '?', email: '' };
  const m = raw.match(/^(.*?)<(.+?)>$/);
  if (m) return { name: m[1].trim() || m[2], email: m[2] };
  return { name: raw, email: raw };
}

/**
 * Destinataires → toujours un tableau de { name, email }.
 * L'API renvoie « a » sous deux formes : une List<String> (MessageComplet) ou
 * une chaîne unique, éventuellement multi-adresses (MessageApercu).
 */
function parseRecipients(raw) {
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : String(raw).split(',');
  return list
    .map(v => (v && typeof v === 'object' ? v : parseFrom(String(v).trim())))
    .filter(r => r.email || r.name);
}

function apiToConv(msg, folder) {
  const isSent = folder === 'sent';
  const { name: fromName, email: fromEmail } = parseFrom(msg.de);
  return {
    id: msg.courrierTrackingId || msg.messageIdImap || ('_' + String(Math.random()).slice(2)),
    sujet: msg.sujet || '(sans objet)',
    participants: isSent
      ? [{ name: msg.a || i18n.t('messagerie:page.destinataire'), email: msg.a || '' }]
      : [{ name: fromName, email: fromEmail }],
    preview: msg.apercu || '',
    date: msg.date,
    unread: !msg.lu && !isSent,
    nature: categorieAffichee(msg),
    sinistre: msg.sinistreNumeroLocal || null,
    count: 1,
    attachments: msg.nbPiecesJointes || 0,
    criticality: msg.criticite || null,
    critDays: msg.joursRestants ?? null,
    delai: msg.delai || null,
    starred: msg.suivi || false,
    snoozed: msg.snooze || false,
    snoozeLabel: msg.snoozeLabel || null,
    draft: folder === 'drafts',
    statutEnvoi: msg.statutEnvoi || null,
    raw: msg,
  };
}

function fallbackConv(conv) {
  return {
    ...conv,
    messages: [{
      id: conv.id + '_0',
      from: conv.participants[0],
      to: parseRecipients(conv.raw?.a),
      dateFull: conv.date,
      dateShort: '',
      body: [conv.preview || i18n.t('messagerie:page.apercuIndisponible')],
      corpsHtml: null,
      piecesJointes: [],
    }],
  };
}

export default function Messagerie() {
  const { t } = useTranslation('messagerie');
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();

  // Lien direct depuis un dossier (« Ouvrir dans la messagerie ») :
  // /messagerie?dossier=sent&courrier=<courrierTrackingId>.
  const lienDirect = React.useRef(new URLSearchParams(location.search).get('courrier'));
  const lienDirectVuChargement = React.useRef(false);
  const [activeFolder, setActiveFolder] = useState(() => {
    const dossier = new URLSearchParams(location.search).get('dossier');
    return dossier && FOLDER_EP[dossier] ? dossier : 'inbox';
  });
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeConvId, setActiveConvId] = useState(null);
  const [activeConv, setActiveConv] = useState(null);
  const [loadingConv, setLoadingConv] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [quickExpanded, setQuickExpanded] = useState(false);
  const [composeWins, setComposeWins] = useState([]);
  const [seq, setSeq] = useState(1);
  const [searchOpen, setSearchOpen] = useState(false);
  /* Recherche serveur : `null` = aucune recherche serveur en cours, l'overlay filtre
     alors les pages déjà chargées. Un tableau = le jeu de résultats renvoyé par IMAP
     pour toute la boîte, et non plus seulement les pages parcourues. */
  const [rechercheConvs, setRechercheConvs] = useState(null);
  const [rechercheLoading, setRechercheLoading] = useState(false);
  const [rechercheErreur, setRechercheErreur] = useState(false);
  const rechercheEnCours = React.useRef(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState('compte');
  const [nonConfig, setNonConfig] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [sinistres, setSinistres] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [ficheSinistre, setFicheSinistre] = useState(null);
  const [ficheLoading, setFicheLoading] = useState(false);
  const ficheCache = React.useRef({});
  // Pagination serveur : la boîte de réception ne renvoie que 25 mails par page.
  const [pageInfo, setPageInfo] = useState({ page: 0, totalPages: 1, totalMessages: 0 });
  const [loadingMore, setLoadingMore] = useState(false);
  // Le dossier de la requête en vol : une réponse arrivée après un changement de
  // dossier ne doit pas écraser la liste du nouveau dossier.
  const requeteEnCours = React.useRef(null);

  // ── Charger un dossier ──────────────────────────────────────────
  /**
   * @param folder dossier à charger
   * @param page   page demandée (0 = plus récents). Ignorée hors boîte de réception.
   * @param append true = accumuler sous la liste courante au lieu de la remplacer.
   */
  const loadFolder = useCallback(async (folder, page = 0, append = false) => {
    const f = folder || activeFolder;
    const jeton = `${f}#${page}#${append ? 'more' : 'reset'}`;
    requeteEnCours.current = jeton;

    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setActiveConvId(null);
      setActiveConv(null);
    }

    try {
      const res = await axiosInstance.get(FOLDER_EP[f], {
        params: estPagine(f) ? { page } : undefined,
      });
      if (requeteEnCours.current !== jeton) return; // réponse périmée
      const lue = lirePage(res.data?.data);
      const arr = lue.messages.filter(Boolean).map(m => apiToConv(m, f));
      // La dédup est indispensable : la pagination IMAP est indexée, pas ancrée sur
      // un identifiant - un mail entrant décale les bornes et fait rechevaucher les pages.
      setConvs(prev => (append ? fusionnerPages(prev, arr) : arr));
      setPageInfo({
        page: lue.page,
        totalPages: lue.totalPages,
        totalMessages: lue.totalMessages,
      });
      setNonConfig(false);
    } catch (e) {
      if (requeteEnCours.current !== jeton) return;
      const msg = e?.response?.data?.message || '';
      if (e?.response?.status === 400 || msg.toLowerCase().includes('non configurée')) {
        setNonConfig(true);
      } else if (e?.response?.status === 404) {
        if (!append) setConvs([]); // endpoint pas encore dispo - liste vide
      } else {
        toast.error(append
          ? i18n.t('messagerie:page.erreurChargementAnciens')
          : i18n.t('messagerie:page.erreurChargement'));
        if (!append) setConvs([]);
      }
      // En échec de « charger plus », on fige la pagination sur la page déjà
      // acquise : réessayer doit redemander la même page, pas sauter la suivante.
    }
    if (append) setLoadingMore(false);
    else setLoading(false);
  }, [activeFolder]);

  /**
   * Recherche serveur sur un dossier paginé.
   *
   * L'overlay ne filtrait que `convs`, c'est-à-dire les pages déjà chargées : chercher
   * un mail ancien ne donnait rien tant qu'on ne l'avait pas fait défiler. L'endpoint
   * accepte un paramètre `recherche` qui interroge IMAP sur la boîte entière.
   *
   * Les dossiers non paginés sont déjà chargés intégralement : leur filtrage local
   * suffit, et `null` laisse l'overlay travailler sur `convs`.
   */
  const rechercherServeur = useCallback(async (q) => {
    const f = activeFolder;
    const terme = (q || '').trim();
    if (!estPagine(f) || terme.length < 2) {
      rechercheEnCours.current = null;
      setRechercheConvs(null);
      setRechercheLoading(false);
      setRechercheErreur(false);
      return;
    }
    const jeton = `${f}#${terme}`;
    rechercheEnCours.current = jeton;
    setRechercheLoading(true);
    setRechercheErreur(false);
    try {
      const res = await axiosInstance.get(FOLDER_EP[f], {
        params: { recherche: terme, page: 0 },
        skipErrorToast: true,
      });
      if (rechercheEnCours.current !== jeton) return; // frappe suivante déjà partie
      const lue = lirePage(res.data?.data);
      setRechercheConvs(lue.messages.filter(Boolean).map(m => apiToConv(m, f)));
    } catch (e) {
      if (rechercheEnCours.current !== jeton) return;
      // Repli sur le filtrage local, mais signalé : sans cela l'utilisateur croirait
      // avoir interrogé toute sa boîte alors qu'il ne voit que les pages chargées.
      setRechercheConvs(null);
      setRechercheErreur(true);
    }
    if (rechercheEnCours.current === jeton) setRechercheLoading(false);
  }, [activeFolder]);

  /** Ferme la recherche et remet l'overlay sur les pages chargées. */
  const fermerRecherche = useCallback(() => {
    rechercheEnCours.current = null;
    setSearchOpen(false);
    setRechercheConvs(null);
    setRechercheLoading(false);
    setRechercheErreur(false);
  }, []);

  /** Charge la tranche suivante et l'ajoute sous la liste courante. */
  const chargerPlus = useCallback(() => {
    if (loadingMore || loading || !resteDesPages(pageInfo)) return;
    loadFolder(activeFolder, pageInfo.page + 1, true);
  }, [loadingMore, loading, pageInfo, activeFolder, loadFolder]);

  useEffect(() => {
    setPageInfo({ page: 0, totalPages: 1, totalMessages: 0 });
    loadFolder(activeFolder, 0, false);
  }, [activeFolder]); // eslint-disable-line

  // ── Charger templates + sinistres pour la composition ──────────
  useEffect(() => {
    axiosInstance.get('/v1/messagerie/templates')
      .then(r => setTemplates(r.data?.data || []))
      .catch(() => { });
    // skipErrorToast : un COURSIER n'a pas SINISTRE_READ et reçoit un 403 ici ; le .catch
    // juste en dessous laisse déjà le sélecteur de composition vide, un bandeau d'erreur n'apprendrait rien à l'agent.
    axiosInstance.get('/v1/sinistres?page=0&size=50', { skipErrorToast: true })
      .then(r => {
        const arr = r.data?.data?.content || r.data?.data || [];
        setSinistres(arr.map(s => ({
          value: s.sinistreTrackingId,
          label: `${s.numeroSinistreLocal || ''} - ${s.paysEmetteurLibelle || ''}`,
          numero: s.numeroSinistreLocal || '',
        })));
      })
      .catch(() => { });
  }, []);

  // ── Ouvrir une conversation ─────────────────────────────────────
  const selectConv = useCallback(async (conv) => {
    setActiveConvId(conv.id);
    const stid = conv.raw?.sinistreTrackingId || null;
    if (!stid) {
      setFicheSinistre(null); setFicheLoading(false);
    } else if (ficheCache.current[stid]) {
      setFicheSinistre(ficheCache.current[stid]); setFicheLoading(false);
    } else {
      setFicheSinistre(null); setFicheLoading(true);
      // skipErrorToast : un COURSIER n'a pas SINISTRE_READ et reçoit un 403 ici ; le .catch
      // juste en dessous laisse déjà le panneau « fiche sinistre » vide, un bandeau d'erreur n'apprendrait rien à l'agent.
      axiosInstance.get(`/v1/sinistres/${stid}`, { skipErrorToast: true })
        .then(r => {
          const s = r.data?.data || null;
          if (s) ficheCache.current[stid] = s;
          setFicheSinistre(s);
        })
        .catch(() => setFicheSinistre(null))
        .finally(() => setFicheLoading(false));
    }
    setExpanded({});
    setQuickExpanded(false);
    setSettingsOpen(false);
    setSearchOpen(false);
    setConvs(prev => prev.map(c => c.id === conv.id ? { ...c, unread: false } : c));
    setLoadingConv(true);
    try {
      let data = null;
      if (conv.raw?.courrierTrackingId) {
        const res = await axiosInstance.get(`/v1/messagerie/messages/${conv.raw.courrierTrackingId}`);
        data = res.data?.data;
      } else if (conv.raw?.messageIdImap) {
        const res = await axiosInstance.get(`/v1/messagerie/imap/${conv.raw.messageIdImap}`);
        data = res.data?.data;
      }
      if (data) {
        const msgIdImap = data.messageIdImap || conv.raw?.messageIdImap || null;
        const msgs = data.messages || [{
          id: conv.id + '_0',
          from: data.de ? parseFrom(data.de) : conv.participants[0],
          to: parseRecipients(data.a ?? conv.raw?.a),
          cc: parseRecipients(data.cc),
          corps: data.corpsTexte || '',
          corpsHtml: data.corpsHtml || null,
          date: data.date,
          // PJ enrichies avec l'identifiant IMAP pour le téléchargement
          piecesJointes: (data.piecesJointes || []).map(p => ({
            id: p.index,
            nom: p.nom || p.nomFichier || 'fichier',
            filename: p.nom || p.nomFichier,
            taille: p.taille || 0,
            contentType: p.contentType,
            messageIdImap: msgIdImap,
          })),
        }];
        setActiveConv({ ...conv, messages: msgs });
      } else {
        setActiveConv(fallbackConv(conv));
      }
    } catch (e) {
      // Catch nu d'origine muet en cas de panne de /messages/{id} ou /imap/{uid} :
      // symptôme observé « pas de pièce jointe, message vide » sans aucune trace.
      console.error('[messagerie] echec chargement conversation', {
        courrierTrackingId: conv.raw?.courrierTrackingId,
        messageIdImap: conv.raw?.messageIdImap,
        status: e?.response?.status,
        data: e?.response?.data,
      });
      setActiveConv(fallbackConv(conv));
    }
    setLoadingConv(false);
  }, []);

  // Lien direct : le message s'ouvre une fois le dossier chargé (le chargement
  // referme la lecture en cours). Absent de la liste, il est ouvert quand même.
  useEffect(() => {
    if (!lienDirect.current) return;
    if (loading) { lienDirectVuChargement.current = true; return; }
    if (!lienDirectVuChargement.current) return;
    const cible = lienDirect.current;
    lienDirect.current = null;
    selectConv(convs.find(c => c.raw?.courrierTrackingId === cible)
      || apiToConv({ courrierTrackingId: cible }, activeFolder));
  }, [loading, convs]); // eslint-disable-line

  // ── Actions ─────────────────────────────────────────────────────
  const handleStar = useCallback((conv, e) => {
    e?.stopPropagation();
    // Le back-end n'étoile que les courriers CBConnect (UUID courrierTrackingId).
    const cid = conv.raw?.courrierTrackingId;
    if (!cid) { pushToast(<StarFilled />, i18n.t('messagerie:page.suiviImpossible')); return; }
    const next = !conv.starred;
    setConvs(prev => prev.map(c => c.id === conv.id ? { ...c, starred: next } : c));
    if (activeConv?.id === conv.id) setActiveConv(a => a ? { ...a, starred: next } : a);
    axiosInstance.patch(`/v1/messagerie/messages/${cid}/star`).catch(() => { });
    pushToast(<StarFilled />, next ? i18n.t('messagerie:page.ajouteSuivis') : i18n.t('messagerie:page.retireSuivis'));
  }, [activeConv]);

  const handleArchive = useCallback((conv, e) => {
    e?.stopPropagation();
    const cid = conv.raw?.courrierTrackingId;
    if (!cid) { pushToast(<CheckCircleOutlined />, i18n.t('messagerie:page.archivageImpossible')); return; }
    setConvs(prev => prev.filter(c => c.id !== conv.id));
    if (activeConvId === conv.id) { setActiveConvId(null); setActiveConv(null); }
    axiosInstance.patch(`/v1/messagerie/messages/${cid}/archive`).catch(() => { });
    pushToast(<CheckCircleOutlined />, i18n.t('messagerie:page.archivee'));
  }, [activeConvId]);

  /* Classement manuel dans une catégorie métier.
     Bascule le message en MATCH_MANUEL côté serveur : plus aucun classement
     automatique ne le réécrira, ce qui rend la correction durable. */
  const handleClasser = useCallback(async (conv, nature) => {
    const cid = conv?.raw?.courrierTrackingId;
    if (!cid) { pushToast(<TagOutlined />, i18n.t('messagerie:page.classementImpossible')); return; }
    const avant = conv.nature;
    // Optimiste : le classement est une action fréquente, attendre l'aller-retour
    // ferait clignoter le sélecteur à chaque choix.
    setConvs(prev => prev.map(c => c.id === conv.id ? { ...c, nature } : c));
    setActiveConv(a => a && a.id === conv.id ? { ...a, nature } : a);
    try {
      await axiosInstance.patch(`/v1/messagerie/messages/${cid}/nature`, { nature });
      pushToast(<TagOutlined />, i18n.t('messagerie:page.categorieEnregistree'));
    } catch (e) {
      setConvs(prev => prev.map(c => c.id === conv.id ? { ...c, nature: avant } : c));
      setActiveConv(a => a && a.id === conv.id ? { ...a, nature: avant } : a);
      pushToast(<TagOutlined />, e?.response?.data?.message || i18n.t('messagerie:page.echecClassement'));
    }
  }, []);

  /* Report d'un message : échéances relatives, calculées côté client puis
     envoyées en ISO. Le backend refuse toute échéance dans le passé. */
  const handleSnooze = useCallback((conv, delai, e) => {
    e?.stopPropagation();
    const uid = conv.raw?.messageIdImap;
    if (!uid) { pushToast(<WarningOutlined />, i18n.t('messagerie:page.reportImpossibleRecus')); return; }

    const d = new Date();
    if (delai === '1h') d.setHours(d.getHours() + 1);
    else if (delai === 'demain') { d.setDate(d.getDate() + 1); d.setHours(8, 0, 0, 0); }
    else if (delai === 'semaine') { d.setDate(d.getDate() + 7); d.setHours(8, 0, 0, 0); }

    // ISO local sans fuseau : le backend attend un LocalDateTime.
    const iso = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString().slice(0, 19);

    setConvs(prev => prev.filter(c => c.id !== conv.id));
    if (activeConvId === conv.id) { setActiveConvId(null); setActiveConv(null); }

    axiosInstance.patch(`/v1/messagerie/imap/${encodeURIComponent(uid)}/snooze?jusquA=${iso}`)
      .then(() => pushToast(<ClockCircleOutlined />, i18n.t('messagerie:page.reporte')))
      .catch(() => { pushToast(<WarningOutlined />, i18n.t('messagerie:page.reportImpossible')); loadFolder(activeFolder); });
  }, [activeConvId, activeFolder]); // eslint-disable-line

  /* Depuis le dossier Snoozés : remettre le message en boîte de réception. */
  const handleUnsnooze = useCallback((conv, e) => {
    e?.stopPropagation();
    const uid = conv.raw?.messageIdImap;
    if (!uid) return;
    setConvs(prev => prev.filter(c => c.id !== conv.id));
    axiosInstance.delete(`/v1/messagerie/imap/${encodeURIComponent(uid)}/snooze`)
      .then(() => pushToast(<InboxOutlined />, i18n.t('messagerie:page.remisEnBoite')))
      .catch(() => { pushToast(<WarningOutlined />, i18n.t('messagerie:page.annulationImpossible')); loadFolder(activeFolder); });
  }, [activeFolder]); // eslint-disable-line

  const handleMarkAllRead = useCallback(() => {
    setConvs(prev => prev.map(c => ({ ...c, unread: false })));
    axiosInstance.patch('/v1/messagerie/messages/mark-all-read').catch(() => { });
    pushToast(<CheckCircleOutlined />, i18n.t('messagerie:page.toutLu'));
  }, []);

  // ── Composition ─────────────────────────────────────────────────
  const openCompose = useCallback((preset = {}) => {
    const id = 'cw' + seq;
    setComposeWins(prev => [...prev, fenetreRedaction(id, preset)]);
    setSeq(s => s + 1);
  }, [seq]);

  const updateWin = useCallback((id, patch) =>
    setComposeWins(prev => prev.map(w => w.id === id ? { ...w, ...patch } : w)), []);

  const closeWin = useCallback((id) =>
    setComposeWins(prev => prev.filter(w => w.id !== id)), []);

  // ── Toasts ──────────────────────────────────────────────────────
  function pushToast(icon, text) {
    const id = 't' + Date.now();
    setToasts(prev => [...prev, { id, icon, text }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }

  // ── Non configuré ────────────────────────────────────────────────
  /* LA MESSAGERIE S'ANCRE SOUS LA BANDEROLE, PAS A inset: 0. Elle occupe tout
     l'ecran - c'est un client de courrier, il a sa propre barre laterale - mais
     le bandeau d'environnement est fixe en haut avec un zIndex de 2000 contre
     200 ici : il recouvrait les 34 premiers pixels, et donc le bouton
     « ← CBConnect » de l'en-tete, qui est le seul chemin de retour vers
     l'application. `--cb-banniere-h` vaut 0 quand il n'y a pas de bandeau. */
  if (nonConfig) return (
    <div style={{
      position: 'fixed', top: 'var(--cb-banniere-h, 0px)', left: 0, right: 0, bottom: 0,
      zIndex: 200, background: '#f8f9fb',
      display: 'flex', flexDirection: 'column', fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
    }}>
      <div style={{
        padding: '12px 20px', background: T.green, display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'rgba(255,255,255,.18)', border: 'none', color: '#fff',
            borderRadius: 6, padding: '5px 12px', cursor: 'pointer',
            fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
          }}
        >
          ← CBConnect
        </button>
        <span style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>{t('page.titre')}</span>
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ maxWidth: 520, textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 64, marginBottom: 20 }}><MailOutlined style={{ fontSize: 64 }} /></div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 12 }}>
            {t('page.nonConfiguree')}
          </h2>
          <p style={{ color: '#6b7280', marginBottom: 28, fontSize: 15, lineHeight: 1.6 }}>
            {t('page.nonConfigureeAide')}
          </p>
          <a href="/messagerie/config" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '12px 28px', borderRadius: 10, background: T.green,
            color: '#fff', fontWeight: 600, textDecoration: 'none', fontSize: 15,
          }}>
            <SettingOutlined /> {t('page.configurer')}
          </a>
        </div>
      </div>
    </div>
  );

  const openWins = composeWins.filter(w => !w.minimized);
  const minWins = composeWins.filter(w => w.minimized);

  return (
    <div style={{
      position: 'fixed', top: 'var(--cb-banniere-h, 0px)', left: 0, right: 0, bottom: 0,
      zIndex: 200,
      display: 'flex', overflow: 'hidden',
      background: T.panel,
      fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
    }}>
      <style>{`
        @keyframes cbpulse{0%,100%{box-shadow:0 0 0 0 rgba(220,38,38,.45)}50%{box-shadow:0 0 0 5px rgba(220,38,38,0)}}
        @keyframes cbshimmer{0%{background-position:-340px 0}100%{background-position:340px 0}}
        @keyframes cbtoastin{from{transform:translateY(12px);opacity:0}to{transform:translateY(0);opacity:1}}
        .cb-scroll::-webkit-scrollbar{width:7px}
        .cb-scroll::-webkit-scrollbar-thumb{background:#d1d5db;border-radius:6px;border:2px solid transparent;background-clip:content-box}
        .cb-scroll::-webkit-scrollbar-thumb:hover{background:#9ca3af;background-clip:content-box}
        .cb-skel{background:linear-gradient(90deg,#eee 25%,#f5f5f5 37%,#eee 63%);background-size:680px 100%;animation:cbshimmer 1.4s infinite linear;border-radius:4px}
        .cb-row:hover{background:#f0fdf4!important}
        .cb-row:hover .cb-actions{opacity:1!important}
        .cb-msg-body p{margin:0 0 11px}
        .cb-msg-body a{color:${T.green};text-decoration:underline}
      `}</style>

      <div style={{
        flex: 1,
        display: 'flex',
        overflow: 'hidden',
      }}>
        {/* Sidebar verte */}
        <SidebarDossiers
          activeFolder={activeFolder}
          onFolderChange={f => {
            setActiveFolder(f);
            fermerRecherche();
            setSettingsOpen(false);
          }}
          onCompose={() => openCompose({})}
          onSearch={() => setSearchOpen(true)}
          onOpenSettings={() => { setSettingsOpen(true); setSearchOpen(false); }}
          convs={convs}
          user={user}
        />

        {/* Zone principale */}
        <div style={{ flex: 1, display: 'flex', minWidth: 0, position: 'relative' }}>

          {/* Vue mail (top bar + list + reader + fiche) */}
          {!settingsOpen && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <TopBar
                onSearch={() => setSearchOpen(true)}
                unreadCount={convs.filter(c => c.unread).length}
                user={user}
              />
              <div style={{ flex: 1, display: 'flex', minWidth: 0, overflow: 'hidden' }}>
                <ListeConversations
                  folder={activeFolder}
                  convs={convs}
                  loading={loading}
                  activeConvId={activeConvId}
                  onSelect={selectConv}
                  onMarkAllRead={handleMarkAllRead}
                  onRefresh={() => loadFolder(activeFolder)}
                  onStar={handleStar}
                  onArchive={handleArchive}
                  onSnooze={handleSnooze}
                  onUnsnooze={handleUnsnooze}
                  hasMore={resteDesPages(pageInfo)}
                  loadingMore={loadingMore}
                  onLoadMore={chargerPlus}
                  totalMessages={pageInfo.totalMessages}
                />
                <LecteurConversation
                  conv={activeConv}
                  loading={loadingConv}
                  expanded={expanded}
                  onToggle={id => setExpanded(p => ({ ...p, [id]: !p[id] }))}
                  quickExpanded={quickExpanded}
                  onQuickToggle={() => setQuickExpanded(q => !q)}
                  onQuickSend={() => { setQuickExpanded(false); pushToast(<CheckCircleOutlined />, t('page.mailEnvoye')); }}
                  onReply={item => {
                    // « item » peut être une conversation (bouton d'en-tête) ou un
                    // message (bouton d'un message) : on en déduit le destinataire.
                    const m = item?.messages ? item.messages[item.messages.length - 1] : item;
                    const dest = m?.from || activeConv?.participants?.[0] || null;
                    openCompose({
                      title: `Re: ${activeConv?.sujet || ''}`,
                      subject: `Re: ${activeConv?.sujet || ''}`,
                      sinistre: activeConv?.sinistre || '',
                      nature: activeConv?.nature || '',
                      to: dest ? [dest] : [],
                    });
                  }}
                  onForward={item => {
                    const m = item?.messages ? item.messages[item.messages.length - 1] : item;
                    const de = m?.from?.name || m?.from?.email
                      || activeConv?.participants?.[0]?.name
                      || activeConv?.participants?.[0]?.email || '';
                    const orig = m?.corpsHtml
                      || (m?.corps ? String(m.corps).replace(/\n/g, '<br>') : '')
                      || (activeConv?.preview || '');
                    const fwd =
                      `<br><br>---------- Message transféré ----------<br>` +
                      `De : ${de}<br>Objet : ${activeConv?.sujet || ''}<br><br>${orig}`;
                    openCompose({
                      title: `Tr: ${activeConv?.sujet || ''}`,
                      subject: `Tr: ${activeConv?.sujet || ''}`,
                      sinistre: activeConv?.sinistre || '',
                      nature: activeConv?.nature || '',
                      body: fwd,
                    });
                  }}
                  onCreateRelance={() => {
                    openCompose({
                      title: `Relance officielle - ${activeConv?.sinistre || ''}`,
                      subject: `[BNCB-CB] RELANCE - ${activeConv?.sinistre || ''} - Délai dépassé`,
                      sinistre: activeConv?.sinistre || '',
                      nature: 'RELANCE',
                      to: activeConv?.participants || [],
                    });
                    pushToast(<EditOutlined />, t('page.compositionRelance'));
                  }}
                  onStar={() => activeConv && handleStar(activeConv)}
                  onClasser={(nature) => activeConv && handleClasser(activeConv, nature)}
                  user={user}
                  folder={activeFolder}
                />
                {activeConv && <FicheSinistre sinistre={ficheSinistre} loading={ficheLoading} />}
              </div>
            </div>
          )}

          {/* Vue paramètres */}
          {settingsOpen && (
            <ParametresMessagerie
              tab={settingsTab}
              onTabChange={setSettingsTab}
              onClose={() => setSettingsOpen(false)}
              templates={templates}
              onToast={pushToast}
              nonConfig={nonConfig}
            />
          )}

          {/* Overlay recherche */}
          {searchOpen && (
            <SearchOverlay
              /* Les résultats serveur priment quand il y en a ; sinon l'overlay
                 filtre les pages déjà chargées, comme avant. */
              convs={rechercheConvs || convs}
              sinistres={sinistres}
              onRecherche={rechercherServeur}
              rechercheServeur={rechercheConvs !== null}
              rechercheLoading={rechercheLoading}
              rechercheErreur={rechercheErreur}
              onSelect={conv => { fermerRecherche(); selectConv(conv); }}
              onClose={fermerRecherche}
            />
          )}
        </div>
      </div>

      {/* Fenêtres compose flottantes */}
      {composeWins.map(w => (
        <ComposeWindow
          key={w.id}
          win={w}
          openIndex={openWins.indexOf(w)}
          minIndex={minWins.indexOf(w)}
          templates={templates}
          sinistres={sinistres}
          onUpdate={patch => updateWin(w.id, patch)}
          onClose={() => closeWin(w.id)}
          onSend={() => {
            closeWin(w.id);
            pushToast(<CheckCircleOutlined />, t('page.mailEnvoyeA', { nom: w.to?.[0]?.name || t('page.destinataireMinuscule') }));
            loadFolder(activeFolder);
          }}
          onDraft={() => { closeWin(w.id); pushToast(<FileTextOutlined />, t('page.brouillonEnregistre')); }}
        />
      ))}

      {/* Toasts */}
      <div style={{
        position: 'fixed', bottom: 20, right: 20, zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end',
        pointerEvents: 'none',
      }}>
        {toasts.map(t => (
          <div key={t.id} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '12px 16px', borderRadius: 10, minWidth: 200, maxWidth: 340,
            background: '#374151', color: '#fff', fontSize: 13, fontWeight: 500,
            boxShadow: '0 8px 24px rgba(0,0,0,.18)', animation: 'cbtoastin .25s ease',
          }}>
            <span style={{ fontSize: 15 }}>{t.icon}</span>
            <span>{t.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
